import { defineComponent as _defineComponent } from 'vue'
import { type DataInstance, type Organizations } from "@/platform-data";
import { ref, computed, watch, onMounted } from "vue";
import { Line } from "vue-chartjs";
import {
	Chart as ChartJS,
	Title,
	Tooltip,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	CategoryScale
} from "chart.js";
import { type Item, colors, sterilizeStrings } from "@/util/chart-utils";

type Orgs = Record<string, DataInstance<Organizations.Model>>;
type Props = {
	filteredOrgs: DataInstance<Organizations.Model>[] | undefined,
	selectedColumns: any | undefined,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'chart',
  props: {
    filteredOrgs: {},
    selectedColumns: {}
  },
  setup(__props: any, { expose: __expose }) {
  __expose();

const props = __props;

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend
);



// Helper function to parse the employee count as a number
const parseEmployeeCount = (count: string | null) => {
	return count ? parseInt(count.replace(/[^0-9]/g, ""), 10) : null;
};

const dataColumns = computed(()=>props.selectedColumns.filter((col: Item)=>col.value))

const getOrganizations = () => {

	const organizations = props.filteredOrgs ?? []; // Use an empty array if undefined
	// Filter out organizations with no employee count
	const organizationsWithEmployees = organizations.filter(
		org => parseEmployeeCount(org["# of Employees"]) !== null
	);

	return organizations;
}

function getDataSet( org: DataInstance<Organizations.Model>){
	let innerObject: (string | number | boolean | Organizations.SocialHandles | null)[] = [];

	dataColumns.value.forEach((col: Item)=>{
		let v = org[col.column as keyof DataInstance<Organizations.Model>] || 0;
		if( typeof v === "string") v = sterilizeStrings(v)
		if(col.value && v != undefined) innerObject.push(v)
	})

	return innerObject
}

/**
 *    @returns {Array<Object>} Organizations' data sets
 *    @property {Array<String|Number>} data organization's data points for x axis
 */
function getOrgsDatasets(organizations:DataInstance<Organizations.Model>[]){
	const orgDataSets = organizations.map((org, index) => {
		return {
			label: org["Organization Name"],
			backgroundColor: `${colors[index]}44`,
			borderColor: `${colors[index]}FF`,
			borderWidth: 1,
			data: getDataSet(org),
		}
	})

	return orgDataSets
}


/**
 * @property {Array<DataInstance<Organizations.Model>>} datasets
 * @property {Array<String>} labels
 * @returns {Array<{labels, datasets>}
 */

const data = computed(() => {
	const organizations = getOrganizations();

	// labels: should be the x-axis selected items
	return {
		labels: dataColumns.value.map((col:Item)=>col.label),
		datasets: getOrgsDatasets(organizations)
	}
});

const options = ref({
	responsive: true,
	interaction: {
		intersect: true,
	},
	layout: {
		padding: {
			bottom: 70, // Increase bottom padding to make room for x-axis labels
		},
	},
	scales: {
		x: {
			stacked: true,
			beginAtZero: true,
			ticks: {
				autoSkip: false, // Ensure all x-axis labels are shown
				maxRotation: 90, // Rotate labels if they overlap
				minRotation: 45, // Rotate labels for better fit
				padding:     10, // Increase space between labels and the axis
			},
			grid: {
				display: false, // Hide grid lines if they clutter the chart
			}
		},
		y: {
			stacked: true,
			beginAtZero: true,
			ticks: {
				suggestedMin: 0,
				max: Math.max(...(data.value.datasets[0].data as number[])) + 10, // Set max value dynamically
			},
		}
	},
});


watch(data, (newData) => {
    options.value.scales.y.ticks.max = Math.max(...(newData.datasets[0].data as number[])) + 10; // Update y-axis max value dynamically
}, { immediate: true });


const __returned__ = { props, parseEmployeeCount, dataColumns, getOrganizations, getDataSet, getOrgsDatasets, data, options, ref, computed, watch, onMounted, get Line() { return Line }, get ChartJS() { return ChartJS }, get Title() { return Title }, get Tooltip() { return Tooltip }, get Legend() { return Legend }, get LineElement() { return LineElement }, get LinearScale() { return LinearScale }, get PointElement() { return PointElement }, get CategoryScale() { return CategoryScale }, get colors() { return colors }, get sterilizeStrings() { return sterilizeStrings } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})